import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import type { ServiceTypeData, StatusData } from '../hooks/useAnalyticsData';

interface ServiceProps {
  data: ServiceTypeData[];
  loading: boolean;
}

interface StatusProps {
  data: StatusData[];
  loading: boolean;
}

const SERVICE_COLORS = ['#0d9488', '#06b6d4', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#64748b', '#f97316'];

function formatAmount(v: number): string {
  if (v >= 10000) return `¥${(v / 10000).toFixed(1)}万`;
  return `¥${v.toLocaleString('zh-CN')}`;
}

function ServiceTooltip({ active, payload }: { active?: boolean; payload?: { payload: ServiceTypeData }[] }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 text-sm">
      <p className="font-semibold text-gray-800 mb-1">{d.name}</p>
      <p className="text-gray-600">订单数：<span className="font-medium text-gray-900">{d.count} 单</span></p>
      <p className="text-gray-600">收入：<span className="font-medium text-emerald-600">{formatAmount(d.amount)}</span></p>
    </div>
  );
}

export function ServiceTypeDistribution({ data, loading }: ServiceProps) {
  const total = data.reduce((s, d) => s + d.amount, 0);

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-800">服务类型分布</h3>
        <p className="text-xs text-gray-400 mt-0.5">按收入金额占比</p>
      </div>
      {loading ? (
        <div className="h-48 flex items-center justify-center">
          <div className="text-sm text-gray-400 animate-pulse">加载中...</div>
        </div>
      ) : data.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-sm text-gray-400">暂无数据</div>
      ) : (
        <div className="flex flex-col lg:flex-row items-center gap-4">
          <div className="shrink-0">
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie data={data} cx="50%" cy="50%" innerRadius={48} outerRadius={72} dataKey="amount" paddingAngle={2}>
                  {data.map((_, i) => (
                    <Cell key={i} fill={SERVICE_COLORS[i % SERVICE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<ServiceTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-2 w-full">
            {data.map((item, i) => (
              <div key={item.id} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: SERVICE_COLORS[i % SERVICE_COLORS.length] }} />
                <span className="text-xs text-gray-600 flex-1 truncate">{item.name}</span>
                <span className="text-xs font-medium text-gray-800">{total > 0 ? Math.round((item.amount / total) * 100) : 0}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusTooltip({ active, payload }: { active?: boolean; payload?: { payload: StatusData }[] }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 text-sm">
      <p className="font-semibold text-gray-800">{d.name}</p>
      <p className="text-gray-600">{d.value} 单</p>
    </div>
  );
}

export function OrderStatusDistribution({ data, loading }: StatusProps) {
  const total = data.reduce((s, d) => s + d.value, 0);

  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-800">订单状态分布</h3>
        <p className="text-xs text-gray-400 mt-0.5">各状态订单比例</p>
      </div>
      {loading ? (
        <div className="h-48 flex items-center justify-center">
          <div className="text-sm text-gray-400 animate-pulse">加载中...</div>
        </div>
      ) : data.length === 0 ? (
        <div className="h-48 flex items-center justify-center text-sm text-gray-400">暂无数据</div>
      ) : (
        <div className="flex flex-col lg:flex-row items-center gap-4">
          <div className="shrink-0">
            <ResponsiveContainer width={160} height={160}>
              <PieChart>
                <Pie data={data} cx="50%" cy="50%" innerRadius={48} outerRadius={72} dataKey="value" paddingAngle={2}>
                  {data.map((item, i) => (
                    <Cell key={i} fill={item.color} />
                  ))}
                </Pie>
                <Tooltip content={<StatusTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-2 w-full">
            {data.map(item => (
              <div key={item.name} className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: item.color }} />
                <span className="text-xs text-gray-600 flex-1">{item.name}</span>
                <span className="text-xs font-medium text-gray-800">{item.value} 单</span>
                <span className="text-xs text-gray-400">{total > 0 ? Math.round((item.value / total) * 100) : 0}%</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
